import React, { useEffect, useRef, useState } from "react";
import { Box, Chip, Notification, Spinner, Text, TextField } from "@telia/teddy";
import _get from "lodash/get";
import { useDispatch, useSelector } from "react-redux";
import { getSimNumberError } from "./simCardUtils";
import type { SimDeliveryType, SimType } from "./simCardUtils";
import { useSimNumberCheck } from "./useSimNumberCheck";
import { FORM_ID } from "../../../../../constants";
import { RootState } from "../../../../../models";
import RadioButtonCards from "../../../checkout/components/molecules/RadioButtonCard/RadioButtonCard";
import ExtraSimButton from "../../../checkout/components/organisms/ChooseSimCard/ExtraSimButton";
import { addExtraSimToCart } from "../../../checkout/state/actions/cartActions";
import { getActiveSubscription } from "../../../checkout/state/selectors/cartSelectors";
import ExtraSimModal from "../../../checkout/views/AddMember/ExtraSimModal";
import { getExtraSimPrices } from "../../state/actions/subscriptionStepsActions";
import {
    getExtraDataSimPrice,
    getExtraTvillingSimPrice,
    getHasExistingExtraDataSim,
} from "../../state/selectors/additionalProductsSelectors";
import { getFormById } from "../../state/selectors/formSelectors";
import { getIsD2DOrTMDealer } from "../../state/selectors/selectors";
import "./ChooseSimCard.less";
import { Input } from "../../../forms";

interface Props {
    children: React.ReactNode;
    isPrepaid?: boolean;
    selectedSim: SimType;
    selectedSimDelivery: SimDeliveryType;
    setSelectedSim: (simType: SimType) => void;
    setSelectedSimDelivery: (delivery: SimDeliveryType) => void;
    setSimNumber: (value: string) => void;
    showErrors?: boolean;
    showKeepCurrent?: boolean;
    simNumber: string;
    externalSimNumberError?: string;
    esimType?: "esim-qr" | "esim-push";
    setEsimType?: (type: "esim-qr" | "esim-push") => void;
    eid?: string;
    setEid?: (eid: string) => void;
    onSimNumberInUseChange?: (inUse: boolean) => void;
}

const ChooseSimCard = ({
    children,
    isPrepaid = false,
    selectedSim,
    selectedSimDelivery,
    setSelectedSim,
    setSelectedSimDelivery,
    setSimNumber,
    showErrors = false,
    showKeepCurrent,
    simNumber,
    externalSimNumberError,
    esimType: externalEsimType,
    setEsimType: externalSetEsimType,
    eid: externalEid,
    setEid: externalSetEid,
    onSimNumberInUseChange,
}: Props) => {
    const dispatch = useDispatch();
    const userForm = useSelector((state: RootState) => getFormById(state, FORM_ID.CUSTOMER_PRODUCT_USER));
    const activeSubscription = useSelector(getActiveSubscription);
    const extraDataSimPrice = useSelector((state: RootState) => getExtraDataSimPrice(state, activeSubscription));
    const extraTvillingSimPrice = useSelector((state: RootState) =>
        getExtraTvillingSimPrice(state, activeSubscription),
    );
    const hasExistingExtraDataSim = useSelector((state: RootState) =>
        getHasExistingExtraDataSim(state, activeSubscription),
    );
    const phoneNumber = _get(userForm, "values.user.number", "").replace(/\s/g, "");
    const [, setShowExtraSimModal] = useState(false);
    const simNumberInputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // eSIM type selection - now available for all subscription types, not just prepaid
    const [localEsimType, setLocalEsimType] = useState<"esim-qr" | "esim-push">("esim-qr");
    const [localEid, setLocalEid] = useState("");
    const [eidError, setEidError] = useState<string | null>(null);

    const esimType = externalEsimType ?? localEsimType;
    const setEsimType = externalSetEsimType ?? setLocalEsimType;
    const eid = externalEid ?? localEid;
    const setEid = externalSetEid ?? setLocalEid;

    const isD2DOrTM = useSelector((state: RootState) => getIsD2DOrTMDealer(state));
    const isPhysicalSimSelected = selectedSim === "sim";
    const isEsimSelected = selectedSim === "esim";
    const isSimFromStoreSelected = selectedSimDelivery === "from_store";

    useEffect(() => {
        // D2D/TM dealers don't stock SIM cards in store, so SIM cards are always sent home.
        if (isD2DOrTM && selectedSimDelivery !== "sent_home") {
            setSelectedSimDelivery("sent_home");
        }
    }, [isD2DOrTM, selectedSimDelivery, setSelectedSimDelivery]);

    useEffect(() => {
        if (selectedSim === "sim" && isSimFromStoreSelected) {
            simNumberInputRef.current?.focus();
        }
    }, [isSimFromStoreSelected, selectedSim]);

    useEffect(() => {
        if (selectedSim === "esim" || selectedSim === "keep_current") {
            setTimeout(() => {
                containerRef.current?.querySelector<HTMLButtonElement>("#sim-card-next-button")?.focus();
            }, 0);
        }
    }, [selectedSim]);

    const isSimSentHomeSelected = selectedSimDelivery === "sent_home";

    const isSimNumberComplete = isPhysicalSimSelected && isSimFromStoreSelected && !getSimNumberError(simNumber);
    const { data: simNumberCheck, isFetching: isCheckingSimNumber } = useSimNumberCheck(simNumber, isSimNumberComplete);

    const isSimNumberInUse = !isCheckingSimNumber && simNumberCheck?.inUse === true;
    const simNumberError =
        getSimNumberError(simNumber) ||
        externalSimNumberError ||
        (isSimNumberInUse ? "SIM-nummeret er allerede i bruk" : undefined);
    const shouldShowSimNumberError = showErrors || !!externalSimNumberError || isSimNumberInUse;

    useEffect(() => {
        onSimNumberInUseChange?.(isSimNumberInUse);
    }, [isSimNumberInUse, onSimNumberInUseChange]);

    const simOptions = [
        { value: "esim", heading: "eSIM" },
        { value: "sim", heading: "Fysisk SIM-kort" },
        ...(showKeepCurrent ? [{ value: "keep_current", heading: "Behold nåværende SIM-kort" }] : []),
    ];

    const handleSimNumberPaste = () => {
        setTimeout(() => {
            containerRef.current?.querySelector<HTMLButtonElement>("#sim-card-next-button")?.focus();
        }, 0);
    };

    return (
        <div ref={containerRef}>
            <Box width="100%" className="choose-sim-card">
                <div className="choose-sim-card__content">
                    <RadioButtonCards
                        options={simOptions}
                        setSelectedValue={(value) => setSelectedSim(value as SimType)}
                        selectedValue={selectedSim}
                    >
                        {/* eSIM type selection - shown directly under eSIM radio button */}
                        {isEsimSelected && (
                            <Box mt="100" ml="600" mb="200">
                                <Chip
                                    type="single"
                                    value={esimType}
                                    onValueChange={(val) => {
                                        if (val) {
                                            const newType = val as "esim-qr" | "esim-push";
                                            setEsimType(newType);
                                            if (newType === "esim-qr") {
                                                setEid("");
                                                setEidError(null);
                                            }
                                        }
                                    }}
                                    variant="choice"
                                >
                                    <Chip.Item value="esim-qr" className="choose-sim-card__delivery-chip">
                                        eSIM QR
                                    </Chip.Item>
                                    <Chip.Item value="esim-push" className="choose-sim-card__delivery-chip">
                                        eSIM Push
                                    </Chip.Item>
                                </Chip>
                                {esimType === "esim-push" && (
                                    <Box mt="200" mb="200">
                                        <TextField>
                                            <TextField.Label>EID</TextField.Label>
                                            <TextField.Input
                                                type="text"
                                                value={eid}
                                                onChange={(e) => {
                                                    const digits = (e.target as HTMLInputElement).value.replace(
                                                        /\D/g,
                                                        "",
                                                    );
                                                    setEid(digits);
                                                    if (digits.length > 0) {
                                                        if (digits.length !== 32) {
                                                            setEidError("EID må være 32 siffer");
                                                        } else if (
                                                            !digits.startsWith("89") &&
                                                            !digits.startsWith("35")
                                                        ) {
                                                            setEidError("EID må starte med 89 eller 35");
                                                        } else {
                                                            setEidError(null);
                                                        }
                                                    } else {
                                                        setEidError(null);
                                                    }
                                                }}
                                                placeholder="(normalt 32 siffer)"
                                            />
                                        </TextField>
                                        {eidError && (
                                            <Text
                                                style={{
                                                    color: "var(--teddy-theme-color-text-status-error)",
                                                    marginTop: "0.5rem",
                                                }}
                                            >
                                                {eidError}
                                            </Text>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        )}
                        {isPhysicalSimSelected && (
                            <Box mt="100" ml="600">
                                {!isPrepaid && !isD2DOrTM && (
                                    <Chip
                                        type="single"
                                        value={selectedSimDelivery}
                                        onValueChange={(val) => {
                                            if (val) {
                                                setSelectedSimDelivery(val as SimDeliveryType);
                                            }
                                        }}
                                        variant="choice"
                                    >
                                        <Chip.Item value="from_store" className="choose-sim-card__delivery-chip">
                                            Fra butikk
                                        </Chip.Item>
                                        <Chip.Item value="sent_home" className="choose-sim-card__delivery-chip">
                                            Sendt hjem
                                        </Chip.Item>
                                    </Chip>
                                )}
                                {isSimFromStoreSelected && (
                                    <Box mt="200" mb="200">
                                        <Input
                                            label="SIM-nummer"
                                            type="text"
                                            inputRef={simNumberInputRef}
                                            value={simNumber}
                                            success={simNumberCheck?.inUse === false}
                                            rightContent={isCheckingSimNumber && <Spinner size="sm" />}
                                            error={shouldShowSimNumberError && !!simNumberError}
                                            helpText={shouldShowSimNumberError ? simNumberError : undefined}
                                            onPaste={handleSimNumberPaste}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                const digits = e.target.value.replace(/\D/g, "");
                                                setSimNumber(digits);
                                            }}
                                        />
                                    </Box>
                                )}
                                {isSimSentHomeSelected && (
                                    <Notification type="tile" variant="information" mt="200" mb="200">
                                        <Notification.Icon />
                                        <Notification.Text>
                                            Det er mulig å endre ønsket leveringsadresse senere på oppsummeringssiden.
                                        </Notification.Text>
                                    </Notification>
                                )}
                            </Box>
                        )}
                    </RadioButtonCards>

                    {/* Ekstra SIM er midlertidig deaktivert. */}
                    {false && (
                        <ExtraSimButton
                            onClick={() => {
                                dispatch(getExtraSimPrices(activeSubscription, phoneNumber)).then(() => {
                                    setShowExtraSimModal(true);
                                });
                            }}
                        />
                    )}
                    {false && (
                        <ExtraSimModal
                            onCancel={() => setShowExtraSimModal(false)}
                            onAdd={async (simType, isEsim) => {
                                if (activeSubscription) {
                                    await dispatch(
                                        addExtraSimToCart(simType, activeSubscription, isEsim, phoneNumber, false),
                                    );
                                }
                            }}
                            extraDataSimPrice={extraDataSimPrice}
                            extraTvillingSimPrice={extraTvillingSimPrice}
                            hasExistingExtraDataSim={hasExistingExtraDataSim}
                        />
                    )}
                </div>
                {children}
            </Box>
        </div>
    );
};

export default ChooseSimCard;
